import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { finalize } from 'rxjs/operators';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ThemeConfig } from 'src/app/shared/models/theme-config';
import { ConstantUtil } from 'src/app/util/constant.util';

export interface ThemeModel {
  id?: string;
  name?: string;
  description?: string;
  status?: 'New' | 'Enable' | 'Disable';
  isDefault?: boolean;
  configJson?: string;
  createTime?: number;
  updateTime?: number;
}

// 使用共享的 ThemeConfig（canonical schema）

@Component({
  selector: 'servicenumber-theme',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    I18nPipe,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzGridModule,
    NzIconModule,
    NzSelectModule,
    TestIdDirective
  ],
  templateUrl: './servicenumber-theme.component.html',
  styleUrls: ['./servicenumber-theme.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServicenumberThemeComponent implements OnInit {
  private readonly http = inject(HttpService);
  private readonly fb = inject(FormBuilder);
  private readonly message = inject(NzMessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  @Input() serviceNumberId: string = '';
  @Input() serviceNumberType: string = '';
  @Input() tenantType: string = '';

  loading = false;
  saving = false;
  configForm!: FormGroup;
  selectedTheme: ThemeModel | null = null;
  sourceThemeId: string | null = null;
  sourceThemeName: string | null = null;
  themeOptions: Array<{ id: string; name: string }> = [];
  selectedThemeId: string | null = null;
  private lastSavedSnapshot = '';

  // 阴影预设：無/淺層/標準/深層/自定義
  shadowPreset: 'none' | 'light' | 'standard' | 'deep' | 'custom' = 'custom';
  shadowCustomValue: string = '';
  readonly shadowPresetValues: Record<'none' | 'light' | 'standard' | 'deep', string> = {
    none: 'none',
    light: '0 2px 8px rgba(0,0,0,0.08)',
    standard: '0 4px 12px rgba(0,0,0,0.12)',
    deep: '0 8px 24px rgba(0,0,0,0.16)'
  };

  ngOnInit(): void {
    this.initForm();
    this.loadThemeOptions();
    if (this.serviceNumberId) {
      this.loadCurrentTheme();
    }
  }

  get hasUnsavedChanges(): boolean {
    return JSON.stringify(this.configForm?.getRawValue?.() ?? {}) !== this.lastSavedSnapshot;
  }

  initForm(): void {
    const def = this.getDefaultThemeConfig();
    this.configForm = this.fb.group({
      primaryColor: [def.primaryColor],
      backgroundColor: [def.backgroundColor],
      headerTextColor: [def.headerTextColor],
      customerBubbleBg: [def.customerBubbleBg],
      customerBubbleText: [def.customerBubbleText],
      agentBubbleBg: [def.agentBubbleBg],
      agentBubbleText: [def.agentBubbleText],
      headerBg: [def.headerBg],
      footerBg: [def.footerBg],
      bubbleRadius: [def.bubbleRadius],
      windowRadius: [def.windowRadius],
      boxShadowMedium: [def.boxShadow.medium],
      fontSizeBase: [def.fontSize.base]
    });
    // 初始化阴影预设状态
    this.updateShadowPresetFromForm();
    this.captureBaseline();
  }

  getDefaultThemeConfig(): ThemeConfig {
    return {
      primaryColor: '#1890FF',
      backgroundColor: '#F5F7FA',
      headerTextColor: '#FFFFFF',
      customerBubbleBg: '#E6F7FF',
      customerBubbleText: '#1F2937',
      agentBubbleBg: '#FAFAFA',
      agentBubbleText: '#1F2937',
      headerBg: '#1890FF',
      footerBg: '#FFFFFF',
      bubbleRadius: '8px',
      windowRadius: '12px',
      boxShadow: { medium: '0 4px 12px rgba(0,0,0,0.15)' },
      fontSize: { base: '16px' }
    };
  }

  // 合并部分配置至默认结构
  private mergeThemeConfig(partial: any): ThemeConfig {
    const def = this.getDefaultThemeConfig();
    return {
      ...def,
      ...(partial || {}),
      boxShadow: { ...def.boxShadow, ...(partial?.boxShadow || {}) },
      fontSize: { ...def.fontSize, ...(partial?.fontSize || {}) }
    } as ThemeConfig;
  }

  // 载入当前服务号主题配置
  loadCurrentTheme(): void {
    this.loading = true;
    this.http
      .post(`${ConstantUtil.TENANT_SERVICENUMBER_THEME_ITEM}/${this.serviceNumberId}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success && res?.data) {
          const data = res.data as any;
          this.sourceThemeId = data?.sourceThemeId ?? data?.theme?.id ?? null;
          this.sourceThemeName = data?.sourceThemeName ?? data?.theme?.name ?? null;
          this.selectedThemeId = this.sourceThemeId ?? null;
          this.selectedTheme =
            data?.theme || (this.sourceThemeId ? { id: this.sourceThemeId, name: this.sourceThemeName ?? undefined } : null);
          let cfg: any = {};
          try {
            cfg = JSON.parse(data?.configJson || '{}');
          } catch {}
          const merged = this.mergeThemeConfig(cfg);
          this.patchForm(merged, true);
        }
      });
  }

  // 表单填充
  private patchForm(cfg: ThemeConfig, resetBaseline: boolean = true): void {
    this.configForm.patchValue({
      primaryColor: cfg.primaryColor,
      backgroundColor: cfg.backgroundColor,
      headerTextColor: cfg.headerTextColor,
      customerBubbleBg: cfg.customerBubbleBg,
      customerBubbleText: cfg.customerBubbleText,
      agentBubbleBg: cfg.agentBubbleBg,
      agentBubbleText: cfg.agentBubbleText,
      headerBg: cfg.headerBg,
      footerBg: cfg.footerBg,
      bubbleRadius: cfg.bubbleRadius,
      windowRadius: cfg.windowRadius,
      boxShadowMedium: cfg.boxShadow.medium,
      fontSizeBase: cfg.fontSize.base
    });
    if (resetBaseline) {
      this.captureBaseline();
    }
    // 根据 boxShadowMedium 更新预设显示
    this.updateShadowPresetFromForm();
    this.cdr.detectChanges();
  }

  // 加载主题列表（仅取 id 与 name）
  loadThemeOptions(): void {
    const params = {
      pageIndex: 0,
      pageSize: 50,
      key: '',
      direction: 'desc',
      refreshTime: Date.now()
    } as any;
    this.http.post(ConstantUtil.BASE_THEME_V1_LIST, params).subscribe((res: any) => {
      const items = (res?.data?.items || []).map((it: any) => ({ id: String(it?.id || ''), name: String(it?.name || '') }));
      this.themeOptions = items;
      this.cdr.detectChanges();
    });
  }

  // 从下拉选择系统主题并应用配置
  onThemeSelected(id: string): void {
    this.selectedThemeId = id || null;
    if (!this.selectedThemeId) return;
    this.applyThemeFromId(this.selectedThemeId);
  }

  // 按主题ID拉取主题配置并覆盖到当前表单
  private applyThemeFromId(id: string): void {
    this.http.post(`${ConstantUtil.BASE_THEME_V1_ITEM}/${id}`, {}).subscribe({
      next: (res: any) => {
        const data = res?.data;
        if (!data) return;
        try {
          const cfgStr = data.configJson || data.config || data.themeJson;
          const cfg = typeof cfgStr === 'string' ? JSON.parse(cfgStr) : cfgStr;
          if (cfg) {
            const merged = this.mergeThemeConfig(cfg);
            this.patchForm(merged, false);
            // 同步源主题信息
            this.sourceThemeId = String(data.id || id);
            this.sourceThemeName = String(data.name || '');
            this.selectedTheme = { id: this.sourceThemeId || undefined, name: this.sourceThemeName || undefined };
            this.cdr.detectChanges();
          }
        } catch {
          // ignore parse error
        }
      }
    });
  }

  // 提交保存
  save(): void {
    this.saving = true;
    const form = this.configForm.value as any;
    const config: ThemeConfig = {
      primaryColor: form.primaryColor,
      backgroundColor: form.backgroundColor,
      headerTextColor: form.headerTextColor,
      customerBubbleBg: form.customerBubbleBg,
      customerBubbleText: form.customerBubbleText,
      agentBubbleBg: form.agentBubbleBg,
      agentBubbleText: form.agentBubbleText,
      headerBg: form.headerBg,
      footerBg: form.footerBg,
      bubbleRadius: form.bubbleRadius,
      windowRadius: form.windowRadius,
      boxShadow: { medium: form.boxShadowMedium },
      fontSize: { base: form.fontSizeBase }
    };

    const payload: any = {
      serviceNumberId: this.serviceNumberId,
      // 若selectedTheme缺少ID，则回退到sourceThemeId
      sourceThemeId: this.selectedTheme?.id ?? this.sourceThemeId,
      configJson: ''
    };
    try {
      payload.configJson = JSON.stringify(config);
    } catch {
      payload.configJson = '';
    }

    this.http
      .post(ConstantUtil.TENANT_SERVICENUMBER_THEME_SAVE, payload)
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success) {
          this.captureBaseline();
          this.message.success(this.i18n.fanyi('servicenumber.theme.save-success'));
        } else {
          this.message.error(res?.msg ?? this.i18n.fanyi('servicenumber.theme.save-failed'));
        }
      });
  }

  // 计算主题名称显示逻辑
  getThemeDisplayName(): string {
    if (this.selectedTheme?.name) return String(this.selectedTheme.name);
    if (this.sourceThemeName) return String(this.sourceThemeName);
    if (this.sourceThemeId) return '未知';
    return this.i18n.fanyi('servicenumber.theme.none');
  }

  // --- 阴影预设逻辑（与主题详情一致）---
  private updateShadowPresetFromForm(): void {
    const m = (this.configForm?.value?.boxShadowMedium || '').trim();
    const matchKey = (Object.keys(this.shadowPresetValues) as Array<'none' | 'light' | 'standard' | 'deep'>).find(
      k => this.shadowPresetValues[k] === m
    );
    if (matchKey) {
      this.shadowPreset = matchKey;
      this.shadowCustomValue = '';
    } else if (m === 'none') {
      this.shadowPreset = 'none';
      this.shadowCustomValue = '';
    } else {
      this.shadowPreset = 'custom';
      this.shadowCustomValue = m;
    }
  }

  onShadowPresetChange(val: 'none' | 'light' | 'standard' | 'deep' | 'custom'): void {
    this.shadowPreset = val;
    if (val === 'custom') {
      if (!this.shadowCustomValue?.trim()) {
        this.shadowCustomValue = this.shadowPresetValues.standard;
      }
      this.configForm.patchValue({ boxShadowMedium: this.shadowCustomValue });
    } else if (val === 'none') {
      this.configForm.patchValue({ boxShadowMedium: 'none' });
    } else {
      // 淺層/標準/深層
      this.configForm.patchValue({ boxShadowMedium: this.shadowPresetValues[val] });
    }
    this.cdr.detectChanges();
  }

  onShadowCustomValueChange(val: string): void {
    this.shadowCustomValue = val;
    if (this.shadowPreset === 'custom') {
      this.configForm.patchValue({ boxShadowMedium: val });
      this.cdr.detectChanges();
    }
  }

  private captureBaseline(): void {
    this.lastSavedSnapshot = JSON.stringify(this.configForm.getRawValue());
    this.configForm.markAsPristine();
    this.configForm.markAsUntouched();
  }
}
